import { type ClassValue, clsx } from "clsx";
import { twMerge } from "tailwind-merge";
import {
  course,
  category,
  subcategory,
} from "./interfaces";
import { apiCall } from "../../src/lib/api";
import { wooOrder } from "../pages/checkout/orderInterface";
import { cartDetails } from "../pages/products/productInterface";
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function searchCourseByTitle(
  courses: course[],
  searchQuery: string
) {
  return courses.filter((course) =>
    course.COURSE_TITLE.toLowerCase().includes(searchQuery)
  );
}
export function searchCategoryByTitle(
  courses: category[],
  searchQuery: string
) {
  const result = courses.filter((course) =>
    course.CategoryName.toLowerCase().includes(searchQuery)
  );
  return result;
}
export function searchSubcategoryByTitle(
  subcategories: subcategory[],
  searchQuery: string
) {
  return subcategories.filter((subcategory) =>
    subcategory.SubCategoryDesc.toLowerCase().includes(
      searchQuery
    )
  );
}

export function generateCartKey(
  length: number = 36
): string {
  const chars =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  let key = "";
  for (let i = 0; i < length; i++) {
    const randomIndex = Math.floor(
      Math.random() * chars.length
    );
    key += chars[randomIndex];
  }
  return key;
}

export async function addToCartRemoveFromWishlist(
  token: string,
  productId: string,
  wishlistId: number
) {
  if (token) {
    const storedArrayString =
      window.localStorage.getItem("wishlistArray");
    let res: { prod_id: number }[] = storedArrayString
      ? JSON.parse(storedArrayString)
      : [];

    // Find the index of the product in the array
    // Assuming you have the productId defined elsewhere
    const index = res.findIndex((item) => {
      return String(item.prod_id) === productId;
    });

    if (index > -1) {
      // Only splice array when item is found
      res.splice(index, 1);
      // Save the updated array back to localStorage
      window.localStorage.setItem(
        "wishlistArray",
        JSON.stringify(res)
      );
    }
    return;
  } else {
    const res = await fetch(
      `${apiCall}/wp-json/yith/wishlist/v1/wishlists/${wishlistId}/product/${productId}`,
      {
        method: "delete",
        headers: {
          "Content-type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!res.ok) {
      throw new Error(`HTTP error: ${res.status}`);
    }
    if (res.status === 422) {
    }
    const response = await res.json();
    return response;
  }
}

export async function getResetLink(email: string) {
  try {
    const response = await fetch(
      `${apiCall}/wp-json/bdpwr/v1/reset-password`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ email: email }),
      }
    );

    const data = await response.json();
    return data;
    // Set profile data to state
  } catch (error) {
    console.error("Error resetting password", error);
  }
}

export function throttle(func: Function, limit: number) {
  let inThrottle: boolean;
  return function (this: any) {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

export async function createSession(order: wooOrder) {
  if (!order) {
    console.log("No order");
    return;
  }
  const hash = await getOrderHash(order);
  const url =
    "https://amadeussabra.com/amadeus/wp-json/custom-api/v1/montypay";
  const requestBody = {
    merchant_key: "32318d2a-0494-11f0-a350-eec6fb93ec40",
    operation: "purchase",
    methods: ["card"],
    parameters: {
      card: {
        bnrg_installm_def: "00",
        bnrg_installm_months: "00",
        bnrg_installm_plan: "03",
      },
    },
    order: {
      number: "order-" + order.number,
      amount: order.total,
      currency: "USD",
      description:
        order.customer_note ||
        "Purchase order from amadeus sabra beauty shop",
    },
    cancel_url: "https://amadeussabra.com/checkout/err",
    success_url: `https://amadeussabra.com/order/${order.id}`,
    customer: {
      name:
        order.billing.first_name +
        " " +
        order.billing.last_name,
      email: order.billing.email,
    },
    billing_address: {
      country: order.billing.country,
      city: order.billing.city,
      address: order.billing.address_1,
      zip: order.billing.postcode,
      phone: order.billing.phone,
    },
    hash: hash, // Use hash as a string
  };
  try {
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok)
      throw new Error("Failed to create session");

    const data = await response.json();
    window.location.href = data.redirect_url;
    console.log("Session Created:", data);
  } catch (err: any) {
    console.error("Error creating session:", err);
  }
}

export async function createShipment(
  order: wooOrder,
  cart: cartDetails
): Promise<any> {
  if (!order || !order.billing) {
    console.error("Invalid order data:", order);
    throw new Error("Invalid order object");
  }

  const url =
    "https://amadeussabra.com/amadeus/wp-json/aramex/v1/create-shipment";

  try {
    const response = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ref1: `order-${order.number}`,
        Line1: order.billing?.address_1,
        destination_city: order.billing?.city,
        destination_country: order.billing?.country,
        customer_name: order.billing.first_name,
        customer_email: order.billing.email,
        customer_phone: order.billing.phone,
        pieces: order.line_items.length,
        weight: {
          Value: cart.items_weight / 1000,
          Unit: "KG",
        },
        value_amount: {
          CurrencyCode: "USD",
          Value: order.total,
        },
        product_group:
          order.billing.country === "LB" ||
          order.shipping.country === "LB"
            ? "DOM"
            : "EXP",
        Description:
          order.customer_note || "No description provided",
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Shipment API error: ${errorText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Error creating shipment:", error);
    throw error;
  }
}
const getOrderHash = async (order: wooOrder) => {
  const orderData = {
    number: order.id,
    total: order.total,
    currency: "USD",
    customer_note: order.customer_note,
  };
  try {
    const response = await fetch(
      "https://amadeussabra.com/amadeus/wp-json/montypay/v1/generate-hash",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(orderData),
      }
    );

    if (!response.ok) {
      throw new Error(
        `HTTP error! Status: ${response.status}`
      );
    }

    const data = await response.json();
    return data.hash;
  } catch (error) {
    console.error("Error fetching hash:", error);
    return null;
  }
};

export function decodeHTML(html: string): string {
  const txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}
